class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

/**
 * 折叠手风琴，适配动画效果
 * 
 * @author Kunn
 */
class AccordionCollapsible extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.summary = this.querySelector('summary');
    this.content = this.summary.nextElementSibling;

    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    this.summary.addEventListener('focusin', this.onSummaryFocusIn.bind(this));

    // initial
    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.openAccordion();
    } else {
      this.mainDetailsToggle.classList.remove('accordion-opening');
      this.summary.setAttribute('aria-expanded', 'false');
      this.updateHeight();
    }
    const resizeObserver = new ResizeObserver(() => this.updateHeight());
    resizeObserver.observe(this);
    resizeObserver.observe(document.body);
  }

  isSummaryFocus() {
    const active = document.activeElement;
    return active === this.summary || this.summary.contains(active);
  }

  restoreSummaryFocus() {
    const active = document.activeElement;
    if (active === this.summary) return;

    const lostToRoot = !active || active === document.body || active === document.documentElement;
    const insideSummary = this.summary.contains(active);
    if (!lostToRoot && !insideSummary) return;

    this.summary.focus({ preventScroll: true });
  }

  onSummaryFocusIn(event) {
    this.updateHeight();
    if (event.target !== this.summary) {
      this.restoreSummaryFocus();
      return;
    }

    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (active === document.body || active === document.documentElement) {
        this.summary.focus({ preventScroll: true });
      }
    });
  }

  updateHeight() {
    const summaryHeight = this.summary?.clientHeight || 0;
    // Hidden parents (e.g. inactive FAQ tab panels) report 0 — avoid locking height to 0px
    if (!summaryHeight) {
      this.mainDetailsToggle.style.height = '';
      return;
    }

    if (this.mainDetailsToggle.classList.contains('accordion-opening')) {
      this.mainDetailsToggle.style.height =
        this.content.clientHeight + summaryHeight + 'px';
    } else {
      this.mainDetailsToggle.style.height = summaryHeight + 'px';
    }
  }

  onSummaryClick(event) {
    event.preventDefault();
    const isOpen = this.mainDetailsToggle.hasAttribute('open');
    if (isOpen) {
      this.closeAccordion();
    } else {
      this.openAccordion();
    }
    this.restoreSummaryFocus();
  }

  openAccordion() {
    const shouldRestoreFocus = this.isSummaryFocus();
    this.mainDetailsToggle.setAttribute('open', '');
    this.mainDetailsToggle.classList.add('accordion-opening');
    this.summary.setAttribute('aria-expanded', 'true');
    this.updateHeight();
    if (shouldRestoreFocus) {
      requestAnimationFrame(() => this.restoreSummaryFocus());
    }
  }

  closeAccordion() {
    this.mainDetailsToggle.classList.remove('accordion-opening');
    this.mainDetailsToggle.style.height = this.summary.clientHeight + 'px';
    this.summary.setAttribute('aria-expanded', 'false');
    this.closeAnimation();
  }

  closeAnimation() {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        this.mainDetailsToggle.removeAttribute('open');
        this.restoreSummaryFocus();
        const parentOpenDetails = this.mainDetailsToggle.parentElement
          ? this.mainDetailsToggle.parentElement.closest('details[open]')
          : null;
        if (parentOpenDetails && typeof trapFocus === 'function') {
          trapFocus(parentOpenDetails, this.summary);
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}
customElements.define('accordion-collapsible', AccordionCollapsible);